import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { MilestoneDetail, Unternehmen } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBestaetigung } from "@kundeninfo/ui";
import { Abschnitt, Auswahl, Chip, Formular, Hinweis, Optionen } from "@kundeninfo/ui/me-design";
import { KiKnopf, LoeschKnopf } from "../../components/meHuellen.js";
import { ErsteSpalte } from "../../components/objektPanel/ErsteSpalte.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { berlinerZeitpunkt } from "../tickets/ticketFormat.js";
import { LinkSearch } from "./LinkSearch.js";
import type { MilestonesClient } from "./milestonesClient.js";
import { MILESTONE_TEXT } from "./milestoneTexts.js";
import "./MilestonePanel.css";

/**
 * DAS MEILENSTEIN-PANEL (Spec D21, Empfehlung 590a; Plan Aufgabe 8) — `Modal` rechts (strikt, Kreuz, am Telefon
 * vollflächig, getönter Inhalt kommt von me-dialog). Kopf „Meilenstein“ bzw. „Neuer Meilenstein“, darunter der
 * Unternehmens-Chip in der Hausfarbe (der Titel des Modals ist reiner Text); Felder Unternehmen (nur beim Anlegen),
 * Titel, Stichtag, Zustand, Beschreibung. „Speichern“ ist die Bestätigung der Kopffelder: Anlegen bzw. `updateMilestone`
 * mit der Version, auf der die Eingaben beruhen und NUR den geänderten Feldern; 409 lädt neu, verwirft die Eingaben und
 * sagt es; 422 steht am Feld. „Abbrechen“ (auch Kreuz, Escape) fragt gestapelt, wenn Eingaben offen sind (`darfAbbrechen`).
 *
 * Abschnitt „Tickets“ (nur an einem gespeicherten Meilenstein): sichtbare Tickets als einzeilige Zeilen (K10, 115) — erste
 * Spalte der Verweis (nur Panel), dann Nummer, Titel, Zustand als Kennzeichen und Zuständig, „Lösen“ am Zeilenende;
 * Fortschritt aus `progress` (zählt auch unsichtbare Tickets, T21). „Ticket verknüpfen …“
 * öffnet seitlich die Suche (`LinkSearch`) über Nummer und Titel im Unternehmen des Meilensteins (`listTickets({ company,
 * q })`); die Wahl und „Verknüpfen“ sind die Bestätigung, ein Fehler steht in der Suche (Review G3). Lösen fragt gestapelt
 * (`useBestaetigung`), ein Fehler steht im Panel. Beide schreiben sofort; die
 * Antwort ersetzt Tickets und Verlauf, die Kopfeingaben bleiben (ohne Eingaben übernimmt das Panel auch den neuen Kopf).
 * Sind alle Tickets erledigt und ist der Meilenstein offen, schlägt ein Hinweis „erreicht“ vor (590a); sein Knopf setzt
 * nur das Feld, geschrieben wird mit „Speichern“.
 *
 * Verlauf: zugeklappt, neueste oben (der Server liefert älteste zuerst). `onClose(changed)` meldet, ob das Panel
 * geschrieben hat — die Ansicht darunter lädt dann neu.
 */

export type MilestonePanelProps = {
  client: MilestonesClient; companies: readonly Unternehmen[];
  /** `null` = neu. */
  milestoneId: string | null;
  /** Unternehmen der Ansicht bzw. Arbeitsunternehmen; ohne Vorgabe das erste aktive Unternehmen. */
  defaultCompanyId: string | null;
  /** Über einem Dialog geöffnet (Reiter „Verknüpfungen“): `Modal` steht dann seitlich daneben (me-dialog `unter`). */
  unter?: "seitlich";
  onClose: (changed: boolean) => void;
  /** Der Panelknopf einer Ticketzeile (K10): nach der Rückfrage bei Eingaben bekommt der Aufrufer das Ticket und ob das Panel
   *  geschrieben hat — er schließt das Panel (Bereich) bzw. ersetzt es (Gastgeber, PF8). Ohne: kein Verweis. */
  onOpenTicket?: (ticketId: string, changed: boolean) => void;
  onUngespeichert?: (ja: boolean) => void;
  /** Objektpanel im Gastgeber: die Zurück-Taste schließt es (Entscheid 696, PF8). */
  verlauf?: boolean;
};

type State = MilestoneDetail["state"];
type Row = MilestoneDetail["tickets"][number];
type Head = { companyId: string; title: string; dueDate: string; state: State; description: string };
type Patch = Parameters<MilestonesClient["updateMilestone"]>[2];
type Message = { art: "fehler" | "warnung"; text: string };

const T = MILESTONE_TEXT;
const STATES: readonly State[] = ["OPEN", "REACHED", "CANCELLED"];
const FIELDS = ["companyId", "title", "dueDate", "state", "description"] as const;
const TITLE_MAX = 200;
const DESCRIPTION_MAX = 4000;
const TICKET_STATE_ART: Readonly<Record<Row["stateKind"], "laeuft" | "warnung" | "ok">> =
  { OPEN: "laeuft", WAITING: "warnung", DONE: "ok" };

const headOf = (m: MilestoneDetail): Head =>
  ({ companyId: m.companyId, title: m.title, dueDate: m.dueDate, state: m.state, description: m.description ?? "" });
const orNull = (s: string): string | null => (s.trim() === "" ? null : s.trim());
/** Der Text eines gescheiterten Schreibwegs: die Meldung des Servers, bei 403 ohne Meldung `forbidden`, sonst `sonst`. */
function errorText(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError)) return fallback;
  return e.message !== "" ? e.message : e.status === 403 ? T.forbidden : fallback;
}

/** Nur die geänderten Kopffelder — so wie der Server normalisiert (Titel und Beschreibung getrimmt, leer = `null`). */
function patchOf(b: Head, x: Head): Patch {
  const p: Patch = {};
  if (x.title.trim() !== b.title) p.title = x.title.trim();
  if (x.dueDate !== b.dueDate) p.dueDate = x.dueDate;
  if (x.state !== b.state) p.state = x.state;
  if (orNull(x.description) !== orNull(b.description)) p.description = orNull(x.description);
  return p;
}

export function MilestonePanel(p: MilestonePanelProps): ReactElement {
  const { client } = p;
  const isNew = p.milestoneId === null;
  const activeCompanies = p.companies.filter((c) => c.active);
  const [start] = useState<Head>(() => ({ title: "", dueDate: "", state: "OPEN", description: "",
    companyId: activeCompanies.find((c) => c.id === p.defaultCompanyId)?.id ?? activeCompanies[0]?.id ?? "" }));
  const [d, setD] = useState<MilestoneDetail | null>(null);
  /** Worauf die Eingaben beruhen (Kopf und Version) — Verknüpfen ändert ihn nur ohne offene Eingaben. */
  const [base, setBase] = useState<{ head: Head; version: number } | null>(null);
  const [draft, setDraft] = useState<Head>(start);
  const [loadFailed, setLoadFailed] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const changed = useRef(false);
  const { bestaetigen } = useBestaetigung();

  const dirty = isNew
    ? (Object.keys(start) as (keyof Head)[]).some((k) => draft[k].trim() !== start[k])
    : base !== null && Object.keys(patchOf(base.head, draft)).length > 0;
  const reportDirty = useRef(p.onUngespeichert);
  reportDirty.current = p.onUngespeichert;
  useEffect(() => { reportDirty.current?.(dirty); }, [dirty]);

  /** Ein neuer Stand vom Server: Tickets und Verlauf immer, Kopf und Version nur ohne Eingaben (oder erzwungen). */
  const take = (m: MilestoneDetail, withHead: boolean) => {
    setD(m);
    if (withHead || !dirty) { setBase({ head: headOf(m), version: m.version }); setDraft(headOf(m)); }
  };

  const id = p.milestoneId;
  useEffect(() => {
    if (id === null) return undefined;
    let active = true;
    client.getMilestone(id)
      .then((m) => { if (active) { setD(m); setBase({ head: headOf(m), version: m.version }); setDraft(headOf(m)); } })
      .catch(() => { if (active) setLoadFailed(true); });
    return () => { active = false; };
  }, [client, id]);

  /** Der Ausgang eines gescheiterten Schreibwegs: 409 lädt neu (Eingaben verworfen), 422 am Feld, sonst eine Meldung. */
  async function handleFailure(e: unknown, fallback: string): Promise<void> {
    if (e instanceof ApiError && e.status === 409 && id !== null) {
      try {
        take(await client.getMilestone(id), true);
        setMessage({ art: "warnung", text: T.conflict });
      } catch {
        setMessage({ art: "fehler", text: T.conflictReloadFailed });
      }
      return;
    }
    if (e instanceof ApiError && e.status === 422 && e.fields !== undefined && e.fields.length > 0) {
      const atField: Record<string, string> = {};
      const rest: string[] = [];
      for (const f of e.fields) {
        if ((FIELDS as readonly string[]).includes(f.field)) atField[f.field] ??= f.message;
        else rest.push(f.message);
      }
      setFieldErrors(atField);
      setMessage(rest.length === 0 ? null : { art: "fehler", text: [...new Set(rest)].join("\n") });
      return;
    }
    setMessage({ art: "fehler", text: errorText(e, fallback) });
  }

  /** Ein Sofort-Schreibweg (Verknüpfen, Lösen): die Antwort ersetzt Tickets und Verlauf; `null` oder der Fehlertext —
   *  der Aufrufer zeigt ihn dort, wo gearbeitet wird (Suche bzw. Panel). */
  async function writeNow(work: () => Promise<MilestoneDetail>, fallback: string): Promise<string | null> {
    setBusy(true);
    setMessage(null);
    try {
      take(await work(), false);
      changed.current = true;
      return null;
    } catch (e) {
      return errorText(e, fallback);
    } finally {
      setBusy(false);
    }
  }

  const titleEmpty = draft.title.trim() === "";
  const saveBlocked = busy || titleEmpty || draft.dueDate === "" || draft.companyId === ""
    || (!isNew && (base === null || !dirty));
  async function save(): Promise<void> {
    if (saveBlocked) return;
    setBusy(true);
    setMessage(null);
    setFieldErrors({});
    try {
      if (id === null) {
        await client.createMilestone({ companyId: draft.companyId, title: draft.title.trim(), dueDate: draft.dueDate,
          state: draft.state, description: orNull(draft.description) });
      } else {
        await client.updateMilestone(id, base!.version, patchOf(base!.head, draft));
      }
      changed.current = true;
      p.onClose(true);
    } catch (e) {
      await handleFailure(e, T.saveError);
      setBusy(false);
    }
  }
  async function cancel(): Promise<void> {
    if (busy) return;
    if (await darfAbbrechen(dirty)) p.onClose(changed.current);
  }
  async function openTicket(ticketId: string): Promise<void> {
    const open = p.onOpenTicket;
    if (open === undefined || busy || !(await darfAbbrechen(dirty))) return;
    open(ticketId, changed.current);
  }
  async function unlink(t: Row): Promise<void> {
    if (d === null || !(await bestaetigen({ titel: T.unlinkQuestion(t.displayNumber), text: T.unlinkDetail,
                                           bestaetigenText: T.unlink }))) return;
    const error = await writeNow(() => client.unlinkMilestoneTicket(d.id, t.id), T.unlinkError);
    if (error !== null) setMessage({ art: "fehler", text: error });
  }
  const searchCompany = d?.companyId ?? "";
  const searchTickets = useCallback(async (q: string) => (await client.listTickets({ company: searchCompany, q })).items,
    [client, searchCompany]);

  const update = (part: Partial<Head>) => {
    for (const k of Object.keys(part)) {
      setFieldErrors((old) => { if (old[k] === undefined) return old; const n = { ...old }; delete n[k]; return n; });
    }
    setDraft((old) => ({ ...old, ...part }));
  };
  const company = p.companies.find((c) => c.id === (d?.companyId ?? draft.companyId));
  const shortCode = d?.companyShortCode ?? company?.shortCode ?? null;
  const allDone = d !== null && d.state === "OPEN" && draft.state === "OPEN" && d.progress.total > 0
    && d.progress.done === d.progress.total;
  const showForm = isNew || d !== null;

  return (
    <Modal titel={isNew ? T.titleNew : T.title} breite="mittel" onAbbrechen={() => { void cancel(); }} abbrechenGesperrt={busy}
      {...(p.verlauf === true ? { verlauf: true } : {})}>
      <div className="ms" {...(p.unter === undefined ? {} : { "data-unter": p.unter })}>
        {shortCode !== null && (
          <div className="ms__head">
            <Chip {...hausfarbeChip(p.companies, shortCode)}>{shortCode}</Chip>
            {company !== undefined && <span className="ms__company">{company.name}</span>}
          </div>
        )}
        {loadFailed && <Hinweis art="fehler">{T.loadError}</Hinweis>}
        {message !== null && <Hinweis art={message.art}><span className="ms__message">{message.text}</span></Hinweis>}
        {showForm && (
          <Formular beschriftung="oben">
            {isNew && activeCompanies.length > 1 && (
              <Auswahl beschriftung={T.fields.company} wert={draft.companyId} gesperrt={busy}
                {...(fieldErrors.companyId === undefined ? {} : { fehler: fieldErrors.companyId })}
                onGeaendert={(e) => update({ companyId: e.wert })}>
                {activeCompanies.map((c) => <option key={c.id} value={c.id}>{`${c.shortCode} ${c.name}`}</option>)}
              </Auswahl>
            )}
            <Feld id="ms-title" label={T.fields.title} wert={draft.title} bearbeitbar pflicht max={TITLE_MAX} gesperrt={busy}
              fehler={fieldErrors.title} onChange={(w) => update({ title: w })} />
            <Feld id="ms-due" label={T.fields.dueDate} typ="date" wert={draft.dueDate} bearbeitbar pflicht gesperrt={busy}
              fehler={fieldErrors.dueDate} onChange={(w) => update({ dueDate: w })} />
            <Optionen className="ms__state" beschriftung={T.fields.state} darstellung="segmente" richtung="waagerecht" wert={draft.state}
              gesperrt={busy} {...(fieldErrors.state === undefined ? {} : { fehler: fieldErrors.state })}
              onGeaendert={(e) => { const s = STATES.find((x) => x === e.wert); if (s !== undefined) update({ state: s }); }}>
              {STATES.map((s) => <option key={s} value={s}>{T.states[s]}</option>)}
            </Optionen>
            <Feld id="ms-description" label={T.fields.description} typ="mehrzeilig" zeilen={4} wert={draft.description}
              bearbeitbar max={DESCRIPTION_MAX} gesperrt={busy} fehler={fieldErrors.description}
              onChange={(w) => update({ description: w })} />
          </Formular>
        )}
        {allDone && (
          <Hinweis art="info" aktionen={(
            <KiKnopf kompakt deaktiviert={busy} onClick={() => update({ state: "REACHED" })}>{T.markReached}</KiKnopf>
          )}>{T.allDone}</Hinweis>
        )}
        {d !== null && (
          <section className="ms__tickets" aria-label={T.tickets}>
            <div className="ms__section-head">
              <h4 className="ms__heading">{T.tickets}</h4>
              <span className="ms__progress">{T.progress(d.progress.done, d.progress.total)}</span>
              <KiKnopf kompakt deaktiviert={busy} onClick={() => setSearching(true)}>{T.linkTicket}</KiKnopf>
            </div>
            {d.tickets.length === 0 ? <p className="ms__empty">{T.noTickets}</p> : (
              <ul className="ms__list">
                {d.tickets.map((t) => (
                  <li key={t.id} className="ms__ticket" data-ticket={t.id}>
                    {p.onOpenTicket !== undefined && (
                      <ErsteSpalte verweis={{ bezug: { art: "ticket", id: t.id, label: `${t.displayNumber} ${t.title}` },
                        eigenesPanel: () => { void openTicket(t.id); }, ohneSprung: true }} />
                    )}
                    <span className="ms__number">{t.displayNumber}</span>
                    <span className="ms__title">{t.title}</span>
                    <Chip className="ms__ticket-state" statusArt={TICKET_STATE_ART[t.stateKind]}>{T.ticketStates[t.stateKind]}</Chip>
                    <span className="ms__assignee">{t.assignee?.name ?? T.nobody}</span>
                    <LoeschKnopf name={T.unlinkName(t.displayNumber)} deaktiviert={busy} onClick={() => { void unlink(t); }} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        {d !== null && (
          <Abschnitt titel={T.history} zaehler={d.events.length} offen={historyOpen}
            onUmgeschaltet={(e) => setHistoryOpen(e.offen)}>
            {d.events.length === 0 ? <p className="ms__empty">{T.noHistory}</p> : (
              <ol className="ms__history">
                {[...d.events].reverse().map((e) => <li key={e.id}>{T.eventLine(berlinerZeitpunkt(e.occurredAt), e)}</li>)}
              </ol>
            )}
          </Abschnitt>
        )}
      </div>
      <ModalFuss speichernText={T.save} speichernGesperrt={saveBlocked} abbrechenGesperrt={busy}
        onSpeichern={() => { void save(); }} onAbbrechen={() => { void cancel(); }} />
      {searching && d !== null && (
        <LinkSearch title={T.linkTicketTitle} searchName={T.ticketSearch} placeholder={T.ticketSearchHint} search={searchTickets}
          immediate={false} label={(t) => T.hit(t.displayNumber, t.title)} linked={new Set(d.tickets.map((t) => t.id))}
          emptyText={T.noHits} onLink={(ticketId) => writeNow(() => client.linkMilestoneTicket(d.id, ticketId), T.linkError)}
          onDone={() => setSearching(false)} />
      )}
    </Modal>
  );
}

